import React, { useEffect, useState } from "react";
import {
  // Service card icons
  FaLaptopCode,
  FaShoppingCart,
  FaGlobe,
  FaArrowRight,
  FaBriefcase,
  FaCalendarAlt,
  FaChartLine,
  // Why Custom section icons
  FaShieldAlt,
  FaCogs,
  FaRocket,
  // Process section icons
  FaSearch,
  FaPaintBrush,
  FaCode,
  // Industry icons
  FaUtensils,
  FaStore,
  FaHeartbeat,
  FaMusic,
  FaMicrochip,
  // FAQ accordion
  FaChevronDown,
  FaStar,
  FaGoogle
} from "react-icons/fa";
import { Link } from "react-router-dom";
import { useInView } from "react-intersection-observer";
import { useTranslation, Trans } from 'react-i18next';
import SEO from '../components/SEO';
import MarketplaceSection from '../components/home/MarketplaceSection';
import SectionHeading from '../components/page/SectionHeading';
import craftImage from '../assets/home/why-craft.webp';
import industriesImage from '../assets/home/industries.webp';
import ctaImage from '../assets/home/cta-harbour.webp';

const oceanVideo = process.env.REACT_APP_OCEAN_VIDEO_URL
  || 'https://riviera-tech-public-assets-eu-north-1.s3.eu-north-1.amazonaws.com/website/home/ocean-video-v1.mp4';

const Home = () => {
  const { t } = useTranslation();
  const googleBusinessProfileUrl = process.env.REACT_APP_GOOGLE_BUSINESS_PROFILE_URL?.trim();
  const sameAsProfiles = [
    "https://github.com/Riviera-Tech-Solutions",
    googleBusinessProfileUrl || null,
  ].filter(Boolean);
  const [isMobile, setIsMobile] = useState(false);
  // FAQ accordion — tracks which question index is open (null = all collapsed)
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  // ── Structured Data (JSON-LD) ──────────────────────────────────────────────
  // Combined graph: Organization + LocalBusiness + FAQPage for rich snippets
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "name": "Riviera Tech Solutions",
        "url": "https://rivieratech.solutions",
        "logo": "https://rivieratech.solutions/RTSLOGO.png",
        "description": "Custom web development, ecommerce platforms, and business websites for companies in Croatia and the Zadar region.",
        "sameAs": sameAsProfiles,
        "contactPoint": {
          "@type": "ContactPoint",
          "contactType": "customer service",
          "availableLanguage": ["en", "hr"]
        }
      },
      {
        "@type": "LocalBusiness",
        "name": "Riviera Tech Solutions",
        "url": "https://rivieratech.solutions",
        "image": "https://rivieratech.solutions/RTSLOGO.png",
        "description": "Custom web development and ecommerce solutions for businesses in Croatia.",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Grabi 26",
          "addressLocality": "Poličnik",
          "addressRegion": "Zadar",
          "addressCountry": "HR"
        },
        "areaServed": [
          { "@type": "Country", "name": "Croatia" },
          { "@type": "AdministrativeArea", "name": "Zadar County" }
        ],
        "priceRange": "€€",
        "sameAs": sameAsProfiles
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "How much does custom web development cost?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Every project is quoted individually after a free 30-minute consultation, because cost depends entirely on scope. Simple static business websites include free hosting."
            }
          },
          {
            "@type": "Question",
            "name": "How long does it take to build a custom website?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "A professional business website typically takes 1–2 weeks. Larger custom web applications like ecommerce platforms or booking systems take 4–8 weeks. Complex full-stack projects may take 2–3 months."
            }
          },
          {
            "@type": "Question",
            "name": "Do you work with small businesses?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Absolutely. Many of our clients are small businesses in Croatia and the Zadar region looking for a professional online presence or a custom tool to streamline their operations."
            }
          },
          {
            "@type": "Question",
            "name": "What happens after launch?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "We provide ongoing support and maintenance for every project. For static business websites, hosting is included free of charge. For larger applications, we offer scalable support packages."
            }
          }
        ]
      }
    ]
  };

  // ── Mobile Detection ───────────────────────────────────────────────────────
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // ── Intersection Observers ─────────────────────────────────────────────────
  // Each section gets its own observer for staggered scroll-reveal animations
  const observerConfig = (threshold = 0.1) => ({
    triggerOnce: true,
    threshold: isMobile ? 0.05 : threshold,
    rootMargin: isMobile ? "50px" : "-50px",
  });

  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: isMobile ? 0.05 : 0.1 });
  const [servicesRef, servicesInView] = useInView(observerConfig(0.2));
  const [whyRef, whyInView] = useInView(observerConfig(0.15));
  const [processRef, processInView] = useInView(observerConfig(0.15));
  const [techRef, techInView] = useInView(observerConfig(0.15));
  const [industriesRef, industriesInView] = useInView(observerConfig(0.15));
  const [projectsRef, projectsInView] = useInView(observerConfig(0.15));
  const [testimonialsRef, testimonialsInView] = useInView(observerConfig(0.15));
  const [faqRef, faqInView] = useInView(observerConfig(0.1));

  // Fallback: force-show content after 2s if intersection observer misfires on some devices
  const [fallbackVisible, setFallbackVisible] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setFallbackVisible(true), 2000);
    return () => clearTimeout(timer);
  }, []);

  // ── Service Cards ──────────────────────────────────────────────────────────
  const serviceCardKeys = ['customWebApp', 'ecommerce', 'businessWebsite', 'portfolio', 'booking', 'consulting'];

  // One accent across the page. Section icons were silver — indistinguishable
  // from body text — so nothing on the page carried the brand colour except
  // the navbar.
  const iconColor = "text-moss-light";
  const serviceIcons = {
    customWebApp: <FaLaptopCode className={`${iconColor} text-5xl mx-auto mb-4`} />,
    ecommerce: <FaShoppingCart className={`${iconColor} text-5xl mx-auto mb-4`} />,
    businessWebsite: <FaGlobe className={`${iconColor} text-5xl mx-auto mb-4`} />,
    portfolio: <FaBriefcase className={`${iconColor} text-5xl mx-auto mb-4`} />,
    booking: <FaCalendarAlt className={`${iconColor} text-5xl mx-auto mb-4`} />,
    consulting: <FaChartLine className={`${iconColor} text-5xl mx-auto mb-4`} />
  };

  // Routes for "See more" links on each service card
  const serviceLinks = {
    customWebApp: '/custom-development',
    consulting: '/consulting',
    businessWebsite: '/business-websites',
    ecommerce: '/ecommerce-coming-soon',
    booking: '/booking-websites'
  };

  // ── Theme Colors ───────────────────────────────────────────────────────────
  const primaryTextColor = "text-silver-light";
  const secondaryTextColor = "text-silver-medium";

  // ── Why Custom Data ────────────────────────────────────────────────────────
  const whyCustomCards = [
    { key: 'ownership', icon: <FaShieldAlt className="text-4xl mb-4 text-moss-light" /> },
    { key: 'builtForYou', icon: <FaCogs className="text-4xl mb-4 text-moss-light" /> },
    { key: 'scalable', icon: <FaRocket className="text-4xl mb-4 text-moss-light" /> },
  ];

  // ── Process Steps ──────────────────────────────────────────────────────────
  const processSteps = [
    { key: 'discovery', icon: <FaSearch className="text-3xl text-moss-light" /> },
    { key: 'design', icon: <FaPaintBrush className="text-3xl text-moss-light" /> },
    { key: 'development', icon: <FaCode className="text-3xl text-moss-light" /> },
    { key: 'launch', icon: <FaRocket className="text-3xl text-moss-light" /> },
  ];

  // ── Tech Stack ─────────────────────────────────────────────────────────────
  const techItems = [
    { name: 'React', category: 'Frontend' },
    { name: 'Next.js', category: 'Frontend' },
    { name: 'Tailwind CSS', category: 'Frontend' },
    { name: 'Node.js', category: 'Backend' },
    { name: 'Express', category: 'Backend' },
    { name: 'MongoDB', category: 'Database' },
    { name: 'PostgreSQL', category: 'Database' },
    { name: 'AWS', category: 'Cloud' },
    { name: 'Docker', category: 'DevOps' },
    { name: 'React Native', category: 'Mobile' },
    { name: 'Electron', category: 'Desktop' },
    { name: 'Git', category: 'Tools' },
  ];

  // ── Industry Cards ─────────────────────────────────────────────────────────
  const industryItems = [
    { key: 'hospitality', icon: <FaUtensils className="text-3xl mb-3 text-moss-light" />, link: '/booking-websites' },
    { key: 'retail', icon: <FaStore className="text-3xl mb-3 text-moss-light" />, link: '/ecommerce-coming-soon' },
    { key: 'professional', icon: <FaBriefcase className="text-3xl mb-3 text-moss-light" />, link: '/business-websites' },
    { key: 'healthcare', icon: <FaHeartbeat className="text-3xl mb-3 text-moss-light" />, link: '/booking-websites' },
    { key: 'creative', icon: <FaMusic className="text-3xl mb-3 text-moss-light" />, link: '/custom-development' },
    { key: 'technology', icon: <FaMicrochip className="text-3xl mb-3 text-moss-light" />, link: '/custom-development' },
  ];

  // ── Project Highlights ─────────────────────────────────────────────────────
  const projectItems = [
    { key: 'arixa', link: '/custom-development' },
    { key: 'accessControl', link: '/consulting' },
    { key: 'clientWebsites', link: '/business-websites' },
  ];

  // ── Testimonials ─────────────────────────────────────────────────────────
  const testimonialKeys = ['client1', 'client2'];

  // ── FAQ Items ──────────────────────────────────────────────────────────────
  const faqKeys = ['cost', 'timeline', 'smallBusiness', 'support'];

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  // ── Analytics ──────────────────────────────────────────────────────────────
  // Pageviews are recorded centrally by <AnalyticsTracker /> in App.js, so
  // every route is counted rather than just the three pages that had a
  // hand-rolled beacon.

  // Helper: visibility class for scroll-reveal sections
  const vis = (inView) => (inView || fallbackVisible) ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0";

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <div className="bg-black font-sans">
      {/* ── SEO Meta ──────────────────────────────────────────────────────── */}
      <SEO
        title="Custom Web Development, Ecommerce Website Development & Business Websites"
        description="Custom website developer and custom web application developer for business growth. We build custom websites, ecommerce website development projects, and custom software with SEO-first architecture and happy-customer outcomes."
        keywords="custom web development, custom web developer, custom web development services, web application development, ecommerce website development, website developer for business, custom software development, custom website developer Croatia, web developer Zadar"
        url="/"
        schema={schema}
      />

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* 1. HERO SECTION                                                   */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      <section
        ref={heroRef}
        className="relative flex items-center justify-center min-h-[80vh] py-20 md:py-32 bg-black overflow-hidden"
      >
        {/* Background video with dark overlay */}
        <video autoPlay loop muted playsInline className="absolute top-0 left-0 w-full h-full object-cover z-0" src={oceanVideo} />
        <div className="absolute top-0 left-0 w-full h-full bg-black/60 z-10"></div>

        <div className="relative text-center px-4 max-w-4xl z-20">
          <p className={`text-moss-light text-sm font-medium tracking-[0.2em] uppercase mb-5 transition-all duration-1000 ${vis(heroInView)}`}>
            {t('home.hero.eyebrow', 'Riviera Tech Solutions')}
          </p>
          <h1 className={`font-serif text-5xl md:text-7xl font-medium ${primaryTextColor} mb-6 transition-all duration-1000 ${vis(heroInView)}`}>
            <Trans i18nKey="home.hero.title">
              Custom Web Development for <span className="text-silver">Businesses That Grow</span>
            </Trans>
          </h1>
          <p className={`text-lg md:text-xl ${primaryTextColor} mb-10 leading-relaxed transition-all duration-1000 delay-200 ${vis(heroInView)}`}>
            {t('home.hero.subtitle')}
          </p>
          <div
            className={`flex flex-col sm:flex-row items-center justify-center gap-5 transition-all duration-1000 ${vis(heroInView)}`}
            style={{ transitionDelay: '400ms' }}
          >
            {/* Limelite funnel — direct product CTA so it isn't buried under the agency hero.
                Stays LIME, not moss: lime is the Limelite sub-brand accent, used
                consistently by the navbar's Limelite item, <MarketplaceSection />
                further down this same page, and every /limelite + /deploy screen.
                Moss is the Riviera Tech marketing accent. Recolouring this button
                would erase the signal that it leads into the product. */}
            <Link
              to="/limelite"
              className="inline-flex items-center gap-2 bg-lime-600 hover:bg-lime-500 text-black font-medium text-base py-3 px-7 rounded-md transition-colors duration-300"
            >
              {t('home.hero.limeliteButton', 'Launch your app on Limelite')}
              <FaArrowRight className="text-xs" />
            </Link>
            <Link
              to="/services"
              className={`relative inline-block text-lg font-medium ${primaryTextColor} group py-2 transition-colors duration-300 hover:text-white`}
            >
              {t('home.hero.exploreButton')}
              <span className="absolute bottom-0 left-0 block w-full h-px bg-silver-medium scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-out origin-left"></span>
            </Link>
          </div>
          {/* Marketplace cross-link — small and secondary, doesn't compete with the Limelite CTA above */}
          <p className={`mt-6 text-sm ${primaryTextColor} opacity-80`}>
            See what people are building?{" "}
            <Link to="/limelite/marketplace" className="underline underline-offset-2 hover:text-lime-400 transition-colors">
              Browse the Limelite Marketplace →
            </Link>
          </p>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* 2. GOOGLE REVIEWS / TESTIMONIALS                                  */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      <section ref={testimonialsRef} className="py-20 bg-off-black">
        <div className="container mx-auto px-4">
          <div className={`text-center mb-12 transition-all duration-1000 transform ${vis(testimonialsInView)}`}>
            <h2 className={`text-3xl md:text-4xl font-serif font-medium mb-1 ${primaryTextColor}`}>{t('home.testimonials.title')}</h2>
            <div className="flex items-center justify-center gap-2 mb-2">
              {[...Array(5)].map((_, i) => (
                <FaStar key={i} className="text-amber-400 text-lg" />
              ))}
              <span className={`${primaryTextColor} font-semibold ml-1`}>{t('home.testimonials.rating')}</span>
            </div>
            <p className={`${secondaryTextColor} text-sm`}>{t('home.testimonials.subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {testimonialKeys.map((key, index) => (
              <div
                key={key}
                className={`transition-all duration-1000 transform ${vis(testimonialsInView)}`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                <div className="bg-black p-6 rounded-lg h-full flex flex-col border border-gray-800 hover:border-gray-700 transition-colors duration-300">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-600 to-gray-800 flex items-center justify-center flex-shrink-0">
                      <span className={`font-semibold text-sm ${primaryTextColor}`}>
                        {t(`home.testimonials.items.${key}.name`).charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${primaryTextColor}`}>
                        {t(`home.testimonials.items.${key}.name`)}
                      </p>
                      <div className="flex gap-0.5">
                        {[...Array(Number(t(`home.testimonials.items.${key}.stars`)))].map((_, i) => (
                          <FaStar key={i} className="text-amber-400 text-xs" />
                        ))}
                      </div>
                    </div>
                    <FaGoogle className="text-silver-dark ml-auto text-sm" />
                  </div>
                  <p className={`${secondaryTextColor} text-sm leading-relaxed flex-grow`}>
                    {t(`home.testimonials.items.${key}.quote`)}
                  </p>
                  <p className="text-xs text-silver-dark mt-4">
                    {t(`home.testimonials.items.${key}.date`)}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className={`text-center mt-10 transition-all duration-1000 transform ${vis(testimonialsInView)}`} style={{ transitionDelay: '450ms' }}>
            <a
              href="https://share.google/dhRZRmo6wwwPEA2bM"
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center gap-2 text-sm font-medium ${primaryTextColor} group py-1 transition-colors duration-300 hover:text-white`}
            >
              <FaGoogle className="text-sm" />
              {t('home.testimonials.viewOnGoogle')}
              <FaArrowRight className="text-xs group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* 3. SERVICES GRID                                                  */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      <section ref={servicesRef} className="py-24 bg-black">
        <div className="container mx-auto px-4 text-center">
          <div className={`transition-all duration-1000 transform ${vis(servicesInView)}`}>
            <SectionHeading
              eyebrow={t('home.services.eyebrow', 'What we build')}
              title={t('home.services.title')}
              className="mb-16"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {serviceCardKeys.map((key, index) => (
              <div
                key={key}
                className={`transition-all duration-1000 transform ${vis(servicesInView)}`}
                style={{ transitionDelay: isMobile ? `${index * 100}ms` : `${index * 150}ms` }}
              >
                <div className="bg-off-black p-8 h-full rounded-lg transition-colors duration-300 flex flex-col">
                  {serviceIcons[key]}
                  <h3 className={`text-xl font-medium mb-4 ${primaryTextColor}`}>{t(`home.services.cards.${key}.title`)}</h3>
                  <p className={`${secondaryTextColor} mb-6 text-sm flex-grow`}>{t(`home.services.cards.${key}.description`)}</p>
                  {serviceLinks[key] && (
                    <Link
                      to={serviceLinks[key]}
                      className={`${secondaryTextColor} hover:text-silver-light text-sm transition-colors duration-300 inline-flex items-center gap-1 mt-auto`}
                    >
                      {t('home.services.learnMore', 'See more')} <FaArrowRight className="text-xs" />
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* 3. WHY CUSTOM WEB DEVELOPMENT                                     */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      <section ref={whyRef} className="py-24 bg-off-black">
        <div className="container mx-auto px-4">
          <div className={`mb-16 transition-all duration-1000 transform ${vis(whyInView)}`}>
            <SectionHeading
              eyebrow={t('home.whyCustom.eyebrow', 'Why custom')}
              title={t('home.whyCustom.title')}
              lead={t('home.whyCustom.subtitle')}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {whyCustomCards.map((card, index) => (
              <div
                key={card.key}
                className={`transition-all duration-1000 transform ${vis(whyInView)}`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                <div className="bg-black p-8 rounded-lg h-full flex flex-col items-center text-center">
                  {card.icon}
                  <h3 className={`text-xl font-medium mb-3 ${primaryTextColor}`}>{t(`home.whyCustom.${card.key}.title`)}</h3>
                  <p className={`${secondaryTextColor} text-sm leading-relaxed`}>{t(`home.whyCustom.${card.key}.description`)}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Internal links for SEO juice */}
          <div className={`flex flex-col sm:flex-row items-center justify-center gap-6 mt-12 transition-all duration-1000 transform ${vis(whyInView)}`} style={{ transitionDelay: '450ms' }}>
            <Link to="/services" className={`relative inline-block text-sm font-medium ${primaryTextColor} group py-1 transition-colors duration-300 hover:text-white`}>
              {t('home.whyCustom.learnMoreServices')}
              <span className="absolute bottom-0 left-0 block w-full h-px bg-silver-medium scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-out origin-left"></span>
            </Link>
            <Link to="/custom-development" className={`relative inline-block text-sm font-medium ${primaryTextColor} group py-1 transition-colors duration-300 hover:text-white`}>
              {t('home.whyCustom.learnMoreCustom')}
              <span className="absolute bottom-0 left-0 block w-full h-px bg-silver-medium scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-out origin-left"></span>
            </Link>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* 3b. CRAFT BAND — a breath between two dense card grids            */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      <section className="relative overflow-hidden border-y border-moss-deep/40">
        <img
          src={craftImage}
          alt=""
          aria-hidden="true"
          className="h-64 w-full object-cover md:h-80"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/75 to-black/30" />
        <div className="absolute inset-0 flex items-center">
          <div className="container mx-auto px-4">
            <p className="max-w-xl text-xl md:text-2xl font-serif text-silver-light leading-snug">
              {t('home.craft.line', 'Built by the engineer who writes the code — not handed down a production line.')}
            </p>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* 4. DEVELOPMENT PROCESS                                            */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      <section ref={processRef} className="py-24 bg-black">
        <div className="container mx-auto px-4">
          <div className={`mb-16 transition-all duration-1000 transform ${vis(processInView)}`}>
            <SectionHeading
              eyebrow={t('home.process.eyebrow', 'How it works')}
              title={t('home.process.title')}
              lead={t('home.process.subtitle')}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {processSteps.map((step, index) => (
              <div
                key={step.key}
                className={`transition-all duration-1000 transform ${vis(processInView)}`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                <div className="bg-off-black p-8 rounded-lg h-full flex flex-col items-center text-center relative">
                  {/* Step number badge */}
                  <span className="text-xs font-mono text-silver-dark mb-4">{t(`home.process.steps.${step.key}.step`)}</span>
                  {step.icon}
                  <h3 className={`text-lg font-medium mt-4 mb-3 ${primaryTextColor}`}>{t(`home.process.steps.${step.key}.title`)}</h3>
                  <p className={`${secondaryTextColor} text-sm leading-relaxed`}>{t(`home.process.steps.${step.key}.description`)}</p>
                </div>
              </div>
            ))}
          </div>

          <div className={`text-center mt-12 transition-all duration-1000 transform ${vis(processInView)}`} style={{ transitionDelay: '600ms' }}>
            <Link to="/contact" className={`relative inline-block text-lg font-medium ${primaryTextColor} group py-2 transition-colors duration-300 hover:text-white`}>
              {t('home.process.ctaButton')}
              <span className="absolute bottom-0 left-0 block w-full h-px bg-silver-medium scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-out origin-left"></span>
            </Link>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* 5. FEATURED PROJECTS (Arixa + YT Library)                         */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      <section className="py-12 bg-black border-t border-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto flex flex-col gap-4">
            <a
              href="https://app.arixa.io"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex flex-col md:flex-row items-center justify-between gap-6 p-6 bg-off-black rounded-lg transition-colors duration-300 border border-gray-800 hover:border-moss/60"
            >
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0">
                  <span className="inline-block rounded-full border border-moss/50 px-3 py-1 text-xs font-medium uppercase tracking-wider text-moss-light">
                    {t('home.featured.badge')}
                  </span>
                </div>
                <div>
                  <h4 className={`text-lg font-medium ${primaryTextColor} group-hover:text-white transition-colors`}>
                    {t('home.featured.arixa.title')}
                  </h4>
                  <p className={`text-sm ${secondaryTextColor}`}>
                    {t('home.featured.arixa.description')}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-moss-light transition-colors">
                <span className="text-sm font-medium">{t('home.featured.arixa.cta')}</span>
                <FaArrowRight className="text-xs group-hover:translate-x-1 transition-transform" />
              </div>
            </a>

            {/* A second card here linked "Get the extension" to
                chromewebstore.google.com/detail/yt-library/placeholder — a
                literal placeholder path, i.e. a dead link on the home page.
                YT Library has a backend in this repo but no listing and no
                front-end route, so there is nothing real to point at yet.
                Restore this card once the extension is actually published. */}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* 6. TECHNOLOGIES WE USE                                            */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      <section ref={techRef} className="py-24 bg-off-black">
        <div className="container mx-auto px-4">
          <div className={`mb-16 transition-all duration-1000 transform ${vis(techInView)}`}>
            <SectionHeading
              eyebrow={t('home.techStack.eyebrow', 'The stack')}
              title={t('home.techStack.title')}
              lead={t('home.techStack.subtitle')}
            />
          </div>

          {/* Tech badges grid */}
          <div className={`flex flex-wrap justify-center gap-3 max-w-4xl mx-auto transition-all duration-1000 transform ${vis(techInView)}`} style={{ transitionDelay: '200ms' }}>
            {techItems.map((tech, index) => (
              <div
                key={tech.name}
                className="px-5 py-3 bg-black rounded-lg border border-gray-800 hover:border-gray-700 transition-colors duration-300"
                style={{ transitionDelay: `${index * 50}ms` }}
              >
                <span className={`text-sm font-medium ${primaryTextColor}`}>{tech.name}</span>
                <span className="text-xs text-silver-dark ml-2">{tech.category}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* 6b. INDUSTRIES BAND                                               */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      <section className="relative overflow-hidden border-y border-moss-deep/40">
        <img
          src={industriesImage}
          alt=""
          aria-hidden="true"
          className="h-64 w-full object-cover md:h-80"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-l from-black via-black/75 to-black/30" />
        <div className="absolute inset-0 flex items-center justify-end">
          <div className="container mx-auto px-4 text-right">
            <p className="ml-auto max-w-xl text-xl md:text-2xl font-serif text-silver-light leading-snug">
              {t('home.industriesBand.line', 'The small businesses that make a place worth visiting — online, and found.')}
            </p>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* 7. INDUSTRIES WE SERVE                                            */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      <section ref={industriesRef} className="py-24 bg-black">
        <div className="container mx-auto px-4">
          <div className={`mb-16 transition-all duration-1000 transform ${vis(industriesInView)}`}>
            <SectionHeading
              eyebrow={t('home.industries.eyebrow', 'Who we work with')}
              title={t('home.industries.title')}
              lead={t('home.industries.subtitle')}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {industryItems.map((item, index) => (
              <Link
                key={item.key}
                to={item.link}
                className={`group transition-all duration-1000 transform ${vis(industriesInView)}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="bg-off-black p-8 rounded-lg h-full flex flex-col group-hover:bg-gray-900 transition-colors duration-300">
                  {item.icon}
                  <h3 className={`text-lg font-medium mb-2 ${primaryTextColor}`}>{t(`home.industries.items.${item.key}.title`)}</h3>
                  <p className={`${secondaryTextColor} text-sm leading-relaxed flex-grow`}>{t(`home.industries.items.${item.key}.description`)}</p>
                  <span className={`${secondaryTextColor} group-hover:text-silver-light text-sm inline-flex items-center gap-1 mt-4 transition-colors duration-300`}>
                    {t('home.services.learnMore', 'Learn more')} <FaArrowRight className="text-xs group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* 8. PROJECT HIGHLIGHTS                                             */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      <section ref={projectsRef} className="py-24 bg-off-black">
        <div className="container mx-auto px-4">
          <div className={`mb-16 transition-all duration-1000 transform ${vis(projectsInView)}`}>
            <SectionHeading
              eyebrow={t('home.projects.eyebrow', 'Selected work')}
              title={t('home.projects.title')}
              lead={t('home.projects.subtitle')}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {projectItems.map((project, index) => (
              <Link
                key={project.key}
                to={project.link}
                className={`group transition-all duration-1000 transform ${vis(projectsInView)}`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                <div className="bg-black p-8 rounded-lg h-full flex flex-col border border-gray-800 group-hover:border-gray-700 transition-colors duration-300">
                  {/* Badge */}
                  <span className="inline-block self-start px-3 py-1 bg-gray-800 text-silver-medium rounded-full text-xs font-medium mb-4">
                    {t(`home.projects.items.${project.key}.badge`)}
                  </span>
                  <h3 className={`text-xl font-medium mb-3 ${primaryTextColor}`}>{t(`home.projects.items.${project.key}.title`)}</h3>
                  <p className={`${secondaryTextColor} text-sm leading-relaxed flex-grow mb-4`}>{t(`home.projects.items.${project.key}.description`)}</p>
                  {/* Tech stack pill */}
                  <p className="text-xs text-silver-dark font-mono">{t(`home.projects.items.${project.key}.tech`)}</p>
                </div>
              </Link>
            ))}
          </div>

          <div className={`text-center mt-12 transition-all duration-1000 transform ${vis(projectsInView)}`} style={{ transitionDelay: '450ms' }}>
            <Link to="/custom-development" className={`relative inline-block text-sm font-medium ${primaryTextColor} group py-1 transition-colors duration-300 hover:text-white`}>
              {t('home.projects.viewAllProjects')}
              <span className="absolute bottom-0 left-0 block w-full h-px bg-silver-medium scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-out origin-left"></span>
            </Link>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* 10. LIMELITE MARKETPLACE                                          */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      <MarketplaceSection />

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* 11. FAQ PREVIEW                                                   */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      <section ref={faqRef} className="py-24 bg-black">
        <div className="container mx-auto px-4">
          <div className={`mb-16 transition-all duration-1000 transform ${vis(faqInView)}`}>
            <SectionHeading
              eyebrow={t('home.faqPreview.eyebrow', 'Common questions')}
              title={t('home.faqPreview.title')}
            />
          </div>

          <div className="max-w-3xl mx-auto">
            {faqKeys.map((key, index) => (
              <div
                key={key}
                className={`border-b border-gray-800 transition-all duration-1000 transform ${vis(faqInView)}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between py-5 text-left cursor-pointer"
                  aria-expanded={openFaqIndex === index}
                >
                  <span className={`text-base font-medium ${primaryTextColor} pr-4`}>
                    {t(`home.faqPreview.items.${key}.question`)}
                  </span>
                  <FaChevronDown className={`text-silver-dark text-sm flex-shrink-0 transition-transform duration-300 ${openFaqIndex === index ? 'rotate-180' : ''}`} />
                </button>
                {/* Collapsible answer panel */}
                <div className={`overflow-hidden transition-all duration-300 ${openFaqIndex === index ? 'max-h-96 pb-5' : 'max-h-0'}`}>
                  <p className={`${secondaryTextColor} text-sm leading-relaxed`}>
                    {t(`home.faqPreview.items.${key}.answer`)}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className={`text-center mt-10 transition-all duration-1000 transform ${vis(faqInView)}`} style={{ transitionDelay: '400ms' }}>
            <Link to="/faq" className={`relative inline-block text-sm font-medium ${primaryTextColor} group py-1 transition-colors duration-300 hover:text-white`}>
              {t('home.faqPreview.viewAllFaq')}
              <span className="absolute bottom-0 left-0 block w-full h-px bg-silver-medium scale-x-0 group-hover:scale-x-100 transition-transform duration-300 ease-out origin-left"></span>
            </Link>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* 11. CTA (Enhanced)                                                */}
      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* The last thing on the page is the ask, and it gets a real button
          rather than the underlined text link it had — every other CTA on the
          page outranked it visually. */}
      <section className="relative overflow-hidden">
        <img
          src={ctaImage}
          alt=""
          aria-hidden="true"
          className="h-[28rem] w-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-black/70" />
        <div className="absolute inset-0 flex items-center">
          <div className="container mx-auto px-4 text-center">
            <div className="mx-auto max-w-3xl">
              <h2 className={`mb-4 text-3xl md:text-4xl font-serif font-medium ${primaryTextColor}`}>{t('home.cta.title')}</h2>
              <p className={`${secondaryTextColor} mb-8`}>{t('home.cta.text')}</p>
              <Link
                to="/contact"
                className="inline-block rounded-md bg-moss px-8 py-3 text-lg font-medium text-black transition-colors duration-300 hover:bg-moss-light"
              >
                {t('home.cta.button')}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
